import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, CardContent, Grid, useTheme, IconButton
} from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Calendar component for visual date selection
const LeaveCalendar = ({ startDate, endDate, onDateSelect }) => {
  const theme = useTheme();
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    const refDate = startDate || endDate;
    if (refDate) {
      const refMonth = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
      if (
        currentMonth.getFullYear() !== refMonth.getFullYear() ||
        currentMonth.getMonth() !== refMonth.getMonth()
      ) {
        setCurrentMonth(refMonth);
      }
    }
  }, [startDate, endDate]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isDateInRange = (date) => {
    if (!startDate || !endDate || !date) return false;
    const dateTime = date.getTime();
    const startTime = startDate.getTime();
    const endTime = endDate.getTime();
    return dateTime >= startTime && dateTime <= endTime;
  };

  const isStartDate = (date) => {
    return startDate && date && date.toDateString() === startDate.toDateString();
  };

  const isEndDate = (date) => {
    return endDate && date && date.toDateString() === endDate.toDateString();
  };

  const isPastDate = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDateClick = (date) => {
    if (isPastDate(date)) return;
    onDateSelect(date);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const days = getDaysInMonth(currentMonth);
  const monthYear = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <Box sx={{ maxHeight: '400px', minWidth: { xs: '100%', sm: 300 } }}>
      <CardContent sx={{ p: '16px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigateMonth(-1)} size="small">
            <ChevronLeft size={20} />
          </IconButton>
          <Typography variant="h6" fontWeight="bold">{monthYear}</Typography>
          <IconButton onClick={() => navigateMonth(1)} size="small">
            <ChevronRight size={20} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <Box key={day} sx={{ textAlign: 'center', fontWeight: 'bold', color: 'text.secondary', fontSize: '0.75rem', py: 1 }}>
              {day}
            </Box>
          ))}

          {days.map((date, index) => {
            const inRange = isDateInRange(date);
            const isStart = isStartDate(date);
            const isEnd = isEndDate(date);

            return (
              <Box key={index} sx={{ position: 'relative', aspectRatio: '1/1' }}>
                {date && (
                  <Box
                    onClick={() => date && handleDateClick(date)}
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      cursor: date && !isPastDate(date) ? 'pointer' : 'default',
                      opacity: isPastDate(date) ? 0.5 : 1,
                      borderRadius: '50%',
                      position: 'relative',
                      zIndex: 1,
                      transition: 'all 0.2s',

                      // Selected state styling
                      ...(isStart || isEnd ? {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        fontWeight: 'bold',
                        boxShadow: 2
                      } : {}),

                      // Hover effect
                      ...(!isStart && !isEnd && date && !isPastDate(date) ? {
                        '&:hover': {
                          bgcolor: 'action.hover',
                        }
                      } : {})
                    }}
                  >
                    {date.getDate()}
                  </Box>
                )}

                {/* Range Background */}
                {inRange && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: 0,
                      right: 0,
                      height: '80%',
                      transform: 'translateY(-50%)',
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.24)' : 'primary.light',
                      opacity: 0.3,
                      zIndex: 0,
                      // Adjust rounded corners for start/end of range
                      ...(isStart && {
                        left: '50%',
                        borderTopLeftRadius: '50%',
                        borderBottomLeftRadius: '50%'
                      }),
                      ...(isEnd && {
                        right: '50%',
                        borderTopRightRadius: '50%',
                        borderBottomRightRadius: '50%'
                      }),
                      // If it's both start and end (single day selected), hide range background or adjust
                      ...(isStart && isEnd && {
                        display: 'none'
                      })
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Box>
  );
};
export default LeaveCalendar;